import { useState, FormEvent } from 'react';

const serviceAreas = [
  { region: 'Northern California', areas: ['Yuba County', 'Sutter County', 'Sacramento', 'Placer County', 'Butte County', 'Nevada County', 'Colusa County', 'Yolo County'] },
  { region: 'Central California', areas: ['San Joaquin County', 'Stanislaus County', 'Fresno County', 'Merced County'] },
  { region: 'Southern California', areas: ['Los Angeles County', 'San Bernardino County', 'Riverside County', 'Orange County'] },
];

export default function ContactServiceAreas() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.message) return;
    if (formData.message.length > 500) return;

    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams({
        'Name': formData.name,
        'email': formData.email,
        'Phone': formData.phone,
        'Message': formData.message,
      }).toString();

      const response = await fetch('https://readdy.ai/api/form/d6bkdqjv53dtlm7j7nfg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: formBody,
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ name: '', email: '', phone: '', message: '' });
      } else {
        setSubmitStatus('error');
      }
    } catch {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact-areas" className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-6">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="flex items-center justify-center gap-3 mb-4">
            <div className="w-10 h-0.5 bg-[#E8740C]"></div>
            <span className="text-[#E8740C] text-xs font-semibold tracking-[0.2em] font-heading uppercase">Contact &amp; Service Areas</span>
            <div className="w-10 h-0.5 bg-[#E8740C]"></div>
          </div>
          <h2 className="font-heading text-4xl md:text-5xl font-bold text-[#1A1A1A] leading-tight mb-4">
            WHERE WE <span className="text-[#E8740C]">SERVE</span>
          </h2>
          <p className="text-[#4A4A4A] text-sm max-w-2xl mx-auto leading-relaxed font-body">
            Based in <strong>Yuba County, California</strong>, New Generation Welding provides mobile welding and steel fabrication services to contractors across Northern &amp; Southern California.
          </p>
        </div>

        {/* Map + Contact Info Row */}
        <div className="grid lg:grid-cols-5 gap-8 mb-16">
          {/* Google Map */}
          <div className="lg:col-span-3 rounded-lg overflow-hidden shadow-md border border-[#E8E6E2]">
            <div className="w-full h-[420px]">
              <iframe
                title="New Generation Welding Service Area - Yuba County California"
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d398940.0!2d-121.6!3d39.14!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x809b5f1e1e1e1e1f%3A0x1e1e1e1e1e1e1e1e!2sYuba%20County%2C%20CA!5e0!3m2!1sen!2sus!4v1700000000000!5m2!1sen!2sus"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                className="w-full h-full"
              ></iframe>
            </div>
          </div>

          {/* Contact Card + Quick Form */}
          <div className="lg:col-span-2 flex flex-col gap-6">
            {/* Contact Details Card */}
            <div className="bg-[#1A1A1A] rounded-lg p-7 flex-shrink-0">
              <h3 className="font-heading font-bold text-white text-lg mb-5">Get In Touch</h3>
              <div className="space-y-4">
                <a href="tel:5308068659" className="flex items-center gap-4 group cursor-pointer">
                  <div className="w-10 h-10 flex items-center justify-center bg-[#E8740C]/20 rounded-lg flex-shrink-0 group-hover:bg-[#E8740C]/30 transition-colors">
                    <i className="ri-phone-fill text-[#E8740C] text-lg"></i>
                  </div>
                  <div>
                    <p className="text-white/40 text-xs">Phone</p>
                    <p className="text-white text-sm font-semibold group-hover:text-[#E8740C] transition-colors">530-806-8659</p>
                  </div>
                </a>
                <a href="mailto:ngweldingfab@gmail.com" className="flex items-center gap-4 group cursor-pointer">
                  <div className="w-10 h-10 flex items-center justify-center bg-[#E8740C]/20 rounded-lg flex-shrink-0 group-hover:bg-[#E8740C]/30 transition-colors">
                    <i className="ri-mail-fill text-[#E8740C] text-lg"></i>
                  </div>
                  <div>
                    <p className="text-white/40 text-xs">Email</p>
                    <p className="text-white text-sm font-semibold group-hover:text-[#E8740C] transition-colors">ngweldingfab@gmail.com</p>
                  </div>
                </a>
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 flex items-center justify-center bg-[#E8740C]/20 rounded-lg flex-shrink-0">
                    <i className="ri-map-pin-fill text-[#E8740C] text-lg"></i>
                  </div>
                  <div>
                    <p className="text-white/40 text-xs">Based In</p>
                    <p className="text-white text-sm font-semibold">Yuba County, California</p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 flex items-center justify-center bg-[#E8740C]/20 rounded-lg flex-shrink-0">
                    <i className="ri-time-fill text-[#E8740C] text-lg"></i>
                  </div>
                  <div>
                    <p className="text-white/40 text-xs">Availability</p>
                    <p className="text-white text-sm font-semibold">24/7 Emergency Service</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Quick Contact Form */}
            <form
              id="contact-quick-form"
              data-readdy-form
              onSubmit={handleSubmit}
              className="bg-[#F8F7F5] rounded-lg p-7 border border-[#E8E6E2] flex-1"
            >
              <h3 className="font-heading font-bold text-[#1A1A1A] text-base mb-4">Quick Message</h3>
              <div className="space-y-3">
                <input
                  type="text"
                  name="name"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Your Name *"
                  className="w-full px-4 py-2.5 border border-[#D4D4D4] rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#E8740C] focus:border-transparent bg-white"
                />
                <input
                  type="email"
                  name="email"
                  required
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="Email Address *"
                  className="w-full px-4 py-2.5 border border-[#D4D4D4] rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#E8740C] focus:border-transparent bg-white"
                />
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  placeholder="Phone (optional)"
                  className="w-full px-4 py-2.5 border border-[#D4D4D4] rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#E8740C] focus:border-transparent bg-white"
                />
                <textarea
                  name="message"
                  required
                  rows={3}
                  maxLength={500}
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  placeholder="How can we help? *"
                  className="w-full px-4 py-2.5 border border-[#D4D4D4] rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#E8740C] focus:border-transparent bg-white resize-none"
                />
                <p className="text-[#9A9A9A] text-xs text-right">{formData.message.length}/500</p>
              </div>
              <button
                type="submit"
                disabled={isSubmitting || formData.message.length > 500}
                className="w-full mt-3 bg-[#E8740C] text-white font-heading font-bold text-sm tracking-wider py-3 rounded-md hover:bg-[#d16a0b] transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
              >
                {isSubmitting ? 'SENDING...' : 'SEND MESSAGE'}
              </button>
              {submitStatus === 'success' && (
                <div className="mt-3 bg-green-50 border border-green-200 rounded-md p-3 text-center">
                  <p className="text-green-700 text-xs font-medium">Message sent! We&apos;ll get back to you shortly.</p>
                </div>
              )}
              {submitStatus === 'error' && (
                <div className="mt-3 bg-red-50 border border-red-200 rounded-md p-3 text-center">
                  <p className="text-red-700 text-xs font-medium">Something went wrong. Please try again.</p>
                </div>
              )}
            </form>
          </div>
        </div>

        {/* Service Areas Grid */}
        <div className="grid md:grid-cols-3 gap-6">
          {serviceAreas.map((region) => (
            <article key={region.region} className="bg-[#F8F7F5] rounded-lg p-7 border border-[#E8E6E2] hover:shadow-md transition-shadow duration-300">
              <div className="flex items-center gap-3 mb-5">
                <div className="w-10 h-10 flex items-center justify-center bg-[#E8740C]/10 rounded-lg">
                  <i className="ri-map-2-fill text-[#E8740C] text-lg"></i>
                </div>
                <h4 className="font-heading font-bold text-[#1A1A1A] text-base">
                  <a href="#request-quote" className="hover:text-[#E8740C] transition-colors cursor-pointer">{region.region}</a>
                </h4>
              </div>
              <ul className="grid grid-cols-2 gap-x-4 gap-y-2">
                {region.areas.map((area) => (
                  <li key={area} className="flex items-center gap-2">
                    <div className="w-4 h-4 flex items-center justify-center flex-shrink-0">
                      <i className="ri-checkbox-circle-fill text-[#E8740C] text-xs"></i>
                    </div>
                    <span className="text-[#4A4A4A] text-xs font-body">{area}</span>
                  </li>
                ))}
              </ul>
            </article>
          ))}
        </div>

        {/* Bottom note */}
        <div className="mt-10 text-center">
          <p className="text-[#6A6A6A] text-xs font-body">
            Don&apos;t see your area? <a href="tel:5308068659" className="text-[#E8740C] font-semibold hover:underline cursor-pointer">Call us at 530-806-8659</a> — we travel statewide for commercial welding and fabrication projects.
          </p>
        </div>
      </div>
    </section>
  );
}
